/**
 * BuoyPanel - Individual Buoy Dashboard Panel Component
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Renders the dashboard panel for a single buoy, including
 * the expand graph functionality, sidebar controls, and content area
 * for displaying charts or data tables.
 * 
 * Responsibilities:
 * - Manage expand/collapse sidebar state
 * - Render buoy-specific header with controls
 * - Coordinate between sidebar controls and main content
 * - Switch between graph and table views
 */

import React, { useState } from 'react';
import SensorChart from '../../solid-chart/SensorChartRefactored';
import DataTable from '../../DataTable';
import SidebarControls from '../../../sidebar/SidebarControls';
import styles from '../SensorDashboard.module.css';

export const BuoyPanel = React.memo(({ 
  buoyId, 
  availableSensors, 
  availableFieldsBySensor, 
  selectedSensors, 
  setSelectedSensors, 
  selectedFields, 
  setSelectedFields, 
  isLoading, 
  viewMode, 
  buoyRows,
  flaggedRows,
  onToggleMerge,
  canMerge = false,
  isMerged = false
}) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #dee2e6',
              background: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            {sidebarExpanded ? '⛶ Expand Graph' : '◀ Show Controls'}
          </button>
          <h3 className={styles.cardTitle}>
            {buoyId} Dashboard
          </h3>
        </div>
      </div>

      <div className={styles.cardContent}>
        {/* Sidebar: Sensors → Fields only */}
        {sidebarExpanded && (
          <aside className={styles.sidebar}>
            <SidebarControls
              buoyId={buoyId}
              availableSensors={availableSensors}
              availableFieldsBySensor={availableFieldsBySensor}
              selectedSensors={selectedSensors[buoyId] || []}
              setSelectedSensors={(sensors) => setSelectedSensors(prev => ({ ...prev, [buoyId]: sensors }))}
              selectedFields={selectedFields[buoyId] || []}
              setSelectedFields={(fields) => setSelectedFields(prev => ({ ...prev, [buoyId]: fields }))}
              isLoading={isLoading}
            />
          </aside>
        )}

        {/* Chart/Table */}
        <div className={styles.contentArea}>
          {viewMode === 'graph' ? (
            <SensorChart
              rawData={buoyRows}
              selectedFields={selectedFields[buoyId] || []}
              onToggleMerge={onToggleMerge}
              canMerge={canMerge}
              isMerged={isMerged}
              onFieldsChange={(fields) => setSelectedFields(prev => ({ ...prev, [buoyId]: fields }))}
            />
          ) : (
            <DataTable
              rawData={buoyRows}
              selectedFields={selectedFields[buoyId] || []}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default BuoyPanel;